import { PATIENT_WAITING_LIST } from "@/api_framework/api_config";
import { IBookingStatusListResponse, IBookingStatusListResponseData } from "@/api_framework/api_modals/Bookings";
import { useCurlLogger } from "@/app/hooks/useCurlLogger";
import axios, { AxiosError, AxiosResponse } from "axios";
import AxiosCurlirize from "axios-curlirize";
import { useCallback } from "react";
import { useAPIUtils } from "../../useAPIUtils";
import { useConfiguration } from "../../useConfiguration";

AxiosCurlirize(axios, () => {});

const normalizeStatusText = (value: unknown): string => String(value ?? "").trim();

const shouldIncludeStatus = (title: string) => {
  const normalizedTitle = title.toLowerCase();
  return normalizedTitle !== "total" && normalizedTitle !== "all" && normalizedTitle !== "all appointments";
};

const toStatusRecord = (title: unknown, id: unknown, fallbackId: string): IBookingStatusListResponseData | null => {
  const safeTitle = normalizeStatusText(title);
  if (!safeTitle || !shouldIncludeStatus(safeTitle)) return null;
  return {
    title: safeTitle,
    id: normalizeStatusText(id) || fallbackId,
  };
};

const normalizeAtHomeBookingStatuses = (data: unknown): IBookingStatusListResponseData[] => {
  const finalStatuses: IBookingStatusListResponseData[] = [];
  const titleSet = new Set<string>();

  const pushStatus = (status: IBookingStatusListResponseData | null) => {
    if (!status) return;
    const statusTitleKey = status.title.toLowerCase();
    if (titleSet.has(statusTitleKey)) return;
    titleSet.add(statusTitleKey);
    finalStatuses.push(status);
  };

  if (Array.isArray(data)) {
    data.forEach((item, index) => {
      if (item && typeof item === "object" && !Array.isArray(item)) {
        const record = item as Record<string, unknown>;
        const mappedStatus = toStatusRecord(
          record.title ?? record.status ?? record.name ?? record.label ?? record.booking_status,
          record.id ?? record.status_id ?? record.value ?? record.booking_status_id,
          `${index + 1}`,
        );

        if (mappedStatus) {
          pushStatus(mappedStatus);
          return;
        }

        Object.entries(record).forEach(([key, value], nestedIndex) => {
          if (value && typeof value === "object" && !Array.isArray(value)) {
            const nestedRecord = value as Record<string, unknown>;
            pushStatus(
              toStatusRecord(
                nestedRecord.title ?? nestedRecord.status ?? nestedRecord.name ?? nestedRecord.label ?? key,
                nestedRecord.id ?? nestedRecord.status_id ?? nestedRecord.value,
                `${index + 1}-${nestedIndex + 1}`,
              ),
            );
            return;
          }

          pushStatus(toStatusRecord(key, value, `${index + 1}-${nestedIndex + 1}`));
        });
        return;
      }

      pushStatus(toStatusRecord(item, undefined, `${index + 1}`));
    });

    return finalStatuses;
  }

  if (data && typeof data === "object") {
    Object.entries(data as Record<string, unknown>).forEach(([key, value], index) => {
      if (value && typeof value === "object" && !Array.isArray(value)) {
        const nestedRecord = value as Record<string, unknown>;
        pushStatus(
          toStatusRecord(
            nestedRecord.title ?? nestedRecord.status ?? nestedRecord.name ?? nestedRecord.label ?? key,
            nestedRecord.id ?? nestedRecord.status_id ?? nestedRecord.value,
            `${index + 1}`,
          ),
        );
        return;
      }

      pushStatus(toStatusRecord(key, value, `${index + 1}`));
    });
  }

  return finalStatuses;
};

export const useAtHomeBookingStatusListAPI = () => {
  const configuration = useConfiguration();
  const { sendCurl } = useCurlLogger();
  const { captureError } = useAPIUtils();

  const execute = useCallback(async (): Promise<IBookingStatusListResponseData[]> => {
    try {
      const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);
      const response: AxiosResponse<IBookingStatusListResponse> = await axios.get(
        PATIENT_WAITING_LIST?.AT_HOME_BOOKING_STATUSES?.baseURL ?? "",
        {
          headers: {
            Authorization: `${accessToken}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.data.status === true) {
        return normalizeAtHomeBookingStatuses((response.data as any)?.data);
      }
      return [];
    } catch (error) {
      const axiosError = error as AxiosError;
      captureError(axiosError);
      sendCurl((axiosError.config as any)?.curlCommand);
      return [];
    }
  }, [captureError, configuration.localStorage.ACCESS_TOKEN.key, sendCurl]);

  return execute;
};
